

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useAuthStore, useDocumentStore, useWorkspaceStore, useUIStore } from "@/stores";
import { api } from "@/lib/api-client";
import { 
  FileText, 
  Folder, 
  Plus, 
  Search, 
  Settings, 
  Moon, 
  Sun, 
  Download,
  Share2,
  Star,
  Archive,
  Trash2,
  Layout,
  LogOut,
} from "lucide-react";
import { useTheme } from "@/lib/theme-provider";
import { toast } from "sonner";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { documents, currentDocument, setCurrentDocument } = useDocumentStore();
  const { workspaces, currentWorkspace, setCurrentWorkspace } = useWorkspaceStore();
  const { user, logout } = useAuthStore();
  const { setTheme } = useTheme();
  const [search, setSearch] = useState("");

  // Filter documents - search in title and content
  const filteredDocuments = useMemo(() => {
    if (!search) return documents.slice(0, 10);
    const query = search.toLowerCase();
    return documents
      .filter((d) => 
        d.title.toLowerCase().includes(query) || 
        (d.plainContent && d.plainContent.toLowerCase().includes(query))
      )
      .slice(0, 15);
  }, [documents, search]);

  // Group documents by workspace
  const groupedDocuments = useMemo(() => {
    const currentWsDocs = filteredDocuments.filter(d => d.workspaceId === currentWorkspace?.id);
    const otherWsDocs = filteredDocuments.filter(d => d.workspaceId !== currentWorkspace?.id);
    return { currentWsDocs, otherWsDocs };
  }, [filteredDocuments, currentWorkspace]);

  // Create new document
  const createDocument = async () => {
    if (!currentWorkspace) {
      toast.error("Selecciona un workspace primero");
      return;
    }

    try {
      const data = await api.post<{ success: boolean; document: any }>('/documents', {
        title: "Nuevo documento",
        workspaceId: currentWorkspace.id,
      });
      if (data.success) {
        setCurrentDocument(data.document);
        onOpenChange(false);
        toast.success("Documento creado");
      }
    } catch (error) {
      toast.error("Error al crear documento");
    }
  };

  // Handle command
  const handleCommand = useCallback((command: string) => {
    switch (command) {
      case "new-document":
        createDocument();
        break;
      case "toggle-theme":
        setTheme("dark");
        toast.info("Tema cambiado");
        break;
      case "logout":
        logout();
        onOpenChange(false);
        break;
      case "import":
        toast.info("Abre la configuración para importar");
        onOpenChange(false);
        break;
      default:
        if (command.startsWith("doc:")) {
          const docId = command.slice(4);
          const doc = documents.find((d) => d.id === docId);
          if (doc) {
            setCurrentDocument(doc);
            onOpenChange(false);
          }
        }
        if (command.startsWith("ws:")) {
          const wsId = command.slice(3);
          const ws = workspaces.find((w) => w.id === wsId);
          if (ws) {
            setCurrentWorkspace(ws);
            onOpenChange(false);
          }
        }
    }
  }, [documents, workspaces, setCurrentDocument, setCurrentWorkspace, onOpenChange, logout, setTheme, createDocument, currentWorkspace]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput 
        placeholder="Escribe un comando o busca..." 
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>No se encontraron resultados</CommandEmpty>

        {/* Quick actions */}
        <CommandGroup heading="Acciones rápidas">
          <CommandItem onSelect={() => handleCommand("new-document")}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo documento
            <CommandShortcut>⌘N</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => handleCommand("import")}>
            <Download className="w-4 h-4 mr-2" />
            Importar vault de Obsidian
          </CommandItem>
          <CommandItem onSelect={() => handleCommand("toggle-theme")}>
            <Moon className="w-4 h-4 mr-2" />
            Cambiar tema
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Workspaces */}
        <CommandGroup heading="Workspaces">
          {workspaces.map((ws) => (
            <CommandItem
              key={ws.id}
              onSelect={() => handleCommand(`ws:${ws.id}`)}
            >
              <Folder className="w-4 h-4 mr-2" />
              {ws.name}
              {ws.id === currentWorkspace?.id && (
                <span className="ml-auto text-xs text-muted-foreground">actual</span>
              )}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Documents in current workspace */}
        {groupedDocuments.currentWsDocs.length > 0 && (
          <>
            <CommandGroup heading={`Documentos en ${currentWorkspace?.name || 'workspace actual'}`}>
              {groupedDocuments.currentWsDocs.map((doc) => (
                <CommandItem
                  key={doc.id}
                  onSelect={() => handleCommand(`doc:${doc.id}`)}
                >
                  <span className="mr-2">{doc.icon || "📄"}</span>
                  {doc.title}
                  {doc.isFavorite && <Star className="w-3 h-3 ml-auto text-yellow-500 fill-yellow-500" />}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Documents in other workspaces */}
        {groupedDocuments.otherWsDocs.length > 0 && (
          <>
            <CommandGroup heading="Otros documentos">
              {groupedDocuments.otherWsDocs.map((doc) => (
                <CommandItem
                  key={doc.id}
                  onSelect={() => handleCommand(`doc:${doc.id}`)}
                >
                  <span className="mr-2">{doc.icon || "📄"}</span>
                  {doc.title}
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({workspaces.find(w => w.id === doc.workspaceId)?.name})
                  </span>
                  {doc.isFavorite && <Star className="w-3 h-3 ml-auto text-yellow-500 fill-yellow-500" />}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandSeparator />

        {/* Settings */}
        <CommandGroup heading="Configuración">
          <CommandItem onSelect={() => toast.info("Abriendo configuración...")}>
            <Settings className="w-4 h-4 mr-2" />
            Preferencias
          </CommandItem>
          <CommandItem onSelect={() => handleCommand("logout")} className="text-destructive">
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
